import React, {useCallback, useEffect, useState} from "react";
import {connect} from "react-redux";
import Col from "@prism/col";
import Row from "@prism/row";
import DeleteSuggestedDamageButton from "./DeleteSuggestedDamageButton";
import AddSuggestedDamageButton from "./AddSuggestedDamageButton";
import {deleteSuggestedDamage, deleteSuggestedDamageImage} from "../../../actions/SuggestedDamageActions";
import {clearSelectedFlatCarPanel} from "../../../actions/flatCarActions";
import FlatCarUtil from "./FlatCar/FlatCarUtil";
import {DamageDropdown, SeverityDropdown} from "./artComponents";
import Dropdown from "../../common/Dropdown";
import isEqual from "lodash.isequal";
import SuggestedDamageImageModal from "./SuggestedDamageImageModal";
import Message from "@prism/message";
import * as pricingUtils from "./PricingUtils";
import SuggestedDamagesTopN from "./suggestedDamagesTopN/SuggestedDamagesTopN";
import TextCounter from "../../common/TextCounter";
import CapturePhotoButton from "./CapturePhotoButton";
import { useCameraCapture } from "../../../hooks/useCameraCapture";
import Confirm from "../../common/Confirm";
import { RETAKE_IMAGE_MESSAGE } from "../../../utils/constants";

const SuggestedDamageLineItem = (props) => {
    const {
        damage,
        deleteSuggestedDamage,
        clearSelectedFlatCarPanel,
        itemList,
        setApiLoading,
        appFlags,
        userDetails
    } = props;
    const [modalOpen, setModalOpen] = useState(false);
    const {panelSuggestedDamages} = FlatCarUtil();

    const [suggestedDamage, setSuggestedDamage] = useState(damage);
    const damageId = suggestedDamage.artCodes.damageId;
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [selectedTopNDamage, setSelectedTopNDamage] = useState(null);
    const [isTopNCustomSelected, setIsTopNCustomSelected] = useState(false);
    const [filteredTopNDamages, setFilteredTopNDamages] = useState(null);
    const [notes, setNotes] = useState(damage?.notes || '');
    const [previousDamageCode, setPreviousDamageCode] = useState(null);

    const { handleCapturePhoto } = useCameraCapture({
        workOrderNumber: props.workOrderInfo.workOrderNumber,
        vin: props.designatedDescription.vin
    });

    useEffect(() => {
        setSuggestedDamage(damage);
    }, [damage]);

    const handleNotesChange = (event) => {
        const newNotes = event.value;
        setNotes(newNotes);
        
        setSuggestedDamage(prevSuggestedDamage => ({
            ...prevSuggestedDamage,
            notes: newNotes
        }));
    };

    const handleDeleteSuggestedDamage = (suggestedDamage) => {
        deleteSuggestedDamage(suggestedDamage);
        if (!panelSuggestedDamages()) clearSelectedFlatCarPanel();
    }

    const handleTopNSelectionChange = (selectedDamageSeverity, isCustom) => {
        console.log("TopN selection changed to:", selectedDamageSeverity);
        setSelectedTopNDamage(selectedDamageSeverity);
        setIsTopNCustomSelected(isCustom === true);
    }

    const handleSelectedItemChange = ({target: {value}}) => {
        // Reset filteredTopNDamages and selection when item changes
        setPreviousDamageCode(suggestedDamage.artCodes.damageCode);
        const selectedIndex = itemList.items.findIndex(item => item.description === value);
        
        setFilteredTopNDamages([]);
        setSelectedTopNDamage(null);

        setSuggestedDamage(prevSuggestedDamage => ({
            ...prevSuggestedDamage,
            artCodes: {
                ...prevSuggestedDamage.artCodes,
                item: value,
                itemCode: itemList.items[selectedIndex].itemCode,
                subItemCode: itemList.items[selectedIndex].subItemCode,
            }
        }));
        updateDamageKey();
    }

    const isDuplicateDamage = useCallback(() => {
        if (!props.condition.damages) {
            return false;
        } else {
            return !!props.condition.damages.find(damage => damage.damageKey === suggestedDamage.newDamageKey)
        }
    }, [props.condition.damages, suggestedDamage]);

    useEffect(() => {
        setIsDuplicate(isDuplicateDamage());
    },[suggestedDamage, isDuplicateDamage]);

    useEffect(() => {
        if (showSuggestedDamagesTopN()) {
            updateDamageKey();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTopNDamage]);

    const updateDamageKey = ()=> {
        setSuggestedDamage(prevSuggestedDamage => ({
            ...prevSuggestedDamage,
            newDamageKey: calculateDamageKey(prevSuggestedDamage, selectedTopNDamage)
        }));
    }

    const calculateDamageKey = (damage, topNDamage = null) => {
        const damageCode = topNDamage && topNDamage.damageCode ? topNDamage.damageCode : damage.artCodes.damageCode;
        return `L1-${damage.artCodes.itemCode}-${damage.artCodes.subItemCode}-${damageCode}`;
    };

    const handleDamageChange = ({target: {options, selectedIndex, value}}) => {
        setSuggestedDamage(prevSuggestedDamage => ({
            ...prevSuggestedDamage,
            artCodes: {
                ...prevSuggestedDamage.artCodes,
                damage: options[selectedIndex].text,
                damageCode: value,
                severity: '',
                severityCode: ''
            }
        }));
        updateDamageKey();
    };

    const handleSeverityChange = ({target: {options, selectedIndex, value}}) => {
        if (options.length > 0) {
            setSuggestedDamage(prevSuggestedDamage => ({
                ...prevSuggestedDamage,
                artCodes: {
                    ...prevSuggestedDamage.artCodes,
                    severity: options[selectedIndex].text,
                    severityCode: value
                }
            }));
        updateDamageKey();
        }
    };

    const handleDamagesLoaded = (damages) => {
        let updateDamageFields = true;
        if (damages.length > 0 && 
            ((previousDamageCode && damages.includes(previousDamageCode)) || 
            !previousDamageCode)) {
            updateDamageFields = false;
        }

        if (updateDamageFields) {
            setSuggestedDamage(prevSuggestedDamage => ({
                ...prevSuggestedDamage,
                artCodes: {
                    ...prevSuggestedDamage.artCodes,
                    damage: '',
                    damageCode: '',
            }
        }));
        }

        if (suggestedDamage && suggestedDamage.topnTypesSeverities) {
            const filteredDamageTypeSeverities = suggestedDamage.topnTypesSeverities
                .map((damage, originalIndex) => ({
                    ...damage,
                    originalIndex // Store the original index position
                }))
                .filter(damage => damage.damageCode && damages.includes(damage.damageCode));
            setFilteredTopNDamages(filteredDamageTypeSeverities);
        }
    };

    const setStatusMessage = (message) => {
        console.log("ERR MSG", message);
    };

    const isEdited = () => {
        if (showSuggestedDamagesTopN()) {
            if (isTopNCustomSelected) {
                return true;
            }
            else if (selectedTopNDamage) {
                return selectedTopNDamage.index !== 0;
            }
        }
        // Default case: compare original damage with current state
        return !isEqual(damage, suggestedDamage);
    }

    const showImage = () => {
        setModalOpen(true);
    };

    const toggleModal = () => {
        setModalOpen((prevState) => !prevState);
    };

    const showSuggestedDamagesTopN = () => {
        const sdTopNUsersArray = appFlags.flags.sdTopNUsers.split(",");
        return !!sdTopNUsersArray.includes(userDetails.userId);
    }

    return (
        <div className="section-container" id={"sd-" + damageId}>
            <SuggestedDamageImageModal
                showModal={modalOpen}
                toggle={toggleModal}
                imageTitle={suggestedDamage.artCodes.item}
                damageKey={suggestedDamage.artCodes.damageKey}
                damageId={damageId}
                imageUrl={suggestedDamage.path}
            />
                <Col xs={12}>
                    <Row className="mt-2">
                        <Col xs={12} className="px-0">
                            <Dropdown onChange={handleSelectedItemChange}
                                      value={suggestedDamage.artCodes.item}
                                      id={'item-dropdown-'+damageId}>
                                      {/* Add initial value to list if not already present */}
                                {(!itemList?.items?.find(option => option.description === suggestedDamage.artCodes.item)) && (
                                    <option
                                        value={suggestedDamage.artCodes.item}>
                                        {suggestedDamage.artCodes.item}
                                    </option>
                                )
                                }
                                {itemList?.items?.map(option => (
                                        <option key={option.code}
                                                value={option.description}>
                                            {option.description}
                                        </option>
                                ))}
                            </Dropdown>
                        </Col>
                    </Row>

                    { showSuggestedDamagesTopN() && (
                        <SuggestedDamagesTopN
                            suggestedDamage={suggestedDamage}
                            setStatusMessage={setStatusMessage}
                            consignment={props.consignment}
                            onSelectionChange={handleTopNSelectionChange}
                            filteredTopNDamages={filteredTopNDamages}
                        />
                    )}

                    <Row className="px-0 mt-2">
                        <Col xs={6} className="px-1">
                            <DamageDropdown
                                id={'damage-dropdown-'+damageId}
                                onChange={handleDamageChange}
                                value={suggestedDamage.artCodes.damageCode}
                                description={suggestedDamage.artCodes.damage}
                                item={{
                                    itemCode: suggestedDamage.artCodes.itemCode,
                                    subItemCode: suggestedDamage.artCodes.subItemCode,
                                }}
                                groupCode={props.account.groupCode}
                                categoryCode={props.consignment.categoryCode}
                                setStatusMessage={setStatusMessage}
                                disabled={showSuggestedDamagesTopN() && !isTopNCustomSelected}
                                onDamagesLoaded={handleDamagesLoaded}
                            />
                        </Col>
                        <Col xs={6} className="px-1">
                            <SeverityDropdown
                                id={'severity-dropdown-'+damageId}
                                onChange={handleSeverityChange}
                                value={suggestedDamage.artCodes.severityCode}
                                item={{
                                    itemCode: suggestedDamage.artCodes.itemCode,
                                    subItemCode: suggestedDamage.artCodes.subItemCode
                                }}
                                description={suggestedDamage.artCodes.severity}
                                damageCode={suggestedDamage.artCodes.damageCode}
                                groupCode={props.account.groupCode}
                                categoryCode={props.consignment.categoryCode}
                                setStatusMessage={setStatusMessage}
                                disabled={isDuplicate || (showSuggestedDamagesTopN() && !isTopNCustomSelected)}
                            />
                        </Col>
                    </Row>
                    
                    <Row className="px-0 mt-2">
                        <Col xs={12} className="px-1">
                            <TextCounter
                                id={'suggested-damage-comments-'+damageId}
                                onChange={handleNotesChange}
                                placeholder="Enter Comments"
                                label="Damage Comments"
                                maxLength={30}
                                rows="1"
                                value={notes}
                            />
                        </Col>
                    </Row>
                    
                    {isDuplicate &&
                        <Message className="mx-1" id="duplicate-suggested-damage-message" color="danger">
                            This damage combination already exists.
                        </Message>
                    }
                    <Row>
                        <Col className="suggested-damage-image-container">
                            {suggestedDamage.path ? (
                                <img className="suggested-damage-image"
                                     id={'suggested-damage-image-' + damageId}
                                     src={suggestedDamage.path} alt={"Vehicle Damage"}
                                     onClick={showImage}/>
                            ) : (
                                <div className="suggested-damage-image media-not-available"
                                     id={'suggested-damage-placeholder-' + damageId}
                                     onClick={showImage}>
                                    <span>Media Not Available</span>
                                </div>
                            )}
                        </Col>
                    </Row>


                    <Row className="suggested-damage-buttons py-2">
                        <Col style={{textAlign: 'left'}}>
                            <DeleteSuggestedDamageButton
                                suggestedDamage={suggestedDamage}
                                onClick={handleDeleteSuggestedDamage}
                            />
                        </Col>
                        <Col style={{textAlign: 'right'}}>
                            <Confirm
                                title="Retake Photo"
                                description={RETAKE_IMAGE_MESSAGE}
                                submitDescription="Retake"
                                cancelDescription="Cancel"
                            >
                                {(confirm) => (
                                    <CapturePhotoButton
                                        id={"retake-sd-image-" + damageId}
                                        className="mx-2 my-0"
                                        color="secondary"
                                        iconClassName="large-button-icon text-white"
                                        onClick={confirm(() => {
                                            // 1. Set suggestedDamage.path to null
                                            props.deleteSuggestedDamageImage(damageId);
                                            // 2. Call native camera to capture photo
                                            handleCapturePhoto(damageId, suggestedDamage.artCodes.item);
                                        })}
                                    />
                                )}
                            </Confirm>
                            <AddSuggestedDamageButton
                                suggestedDamage={suggestedDamage}
                                panelSuggestedDamages={panelSuggestedDamages}
                                isEdited={isEdited()}
                                setApiLoading={setApiLoading}
                                isCRSO={pricingUtils.isCRSO(props.condition)}
                                selectedTopNDamage={selectedTopNDamage}
                                showTopN={showSuggestedDamagesTopN()}
                            />
                        </Col>
                    </Row>
                </Col>
        </div>
    );
};

const mapDispatchToProps = {
    deleteSuggestedDamage,
    deleteSuggestedDamageImage,
    clearSelectedFlatCarPanel
};

function mapStateToProps({itemList, account, consignment, condition, appFlags, workOrderInfo, userDetails, designatedDescription}) {
    return {itemList, account, consignment, condition, appFlags, workOrderInfo, userDetails, designatedDescription};
}

export default connect(mapStateToProps, mapDispatchToProps)(SuggestedDamageLineItem);
